type MentionSplit = {
  mentionnees: string[]
  nonMentionnees: string[]
}

type OfferTranslationSource = {
  job_title?: string | null
  description?: string | null
  description_text?: string | null
  requirements?: unknown
  soft_skills?: unknown
  benefits?: unknown
  certifications?: unknown
  languages?: unknown
  main_missions?: unknown
}

type OfferTranslationEn = {
  job_title?: string
  source_job_title?: string
  description?: string
  source_description?: string
  hard_skills?: unknown[]
  soft_skills?: unknown[]
  certifications?: unknown[]
  benefits?: unknown[]
  languages?: unknown[]
  main_missions?: unknown[]
  hard_skills_non_mentionnées?: unknown[]
  soft_skills_non_mentionnées?: unknown[]
  benefits_non_mentionnées?: unknown[]
  timestamp?: string
}

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => (typeof item === 'string' ? item : String(item ?? '')).trim())
    .filter(Boolean)
}

const extractMentionSplit = (
  source: unknown,
  mentionedKey: string,
  nonMentionedKey: string
): MentionSplit => {
  if (Array.isArray(source)) {
    return {
      mentionnees: toStringList(source),
      nonMentionnees: []
    }
  }

  if (source && typeof source === 'object') {
    const data = source as Record<string, unknown>
    return {
      mentionnees: toStringList(data[mentionedKey]),
      nonMentionnees: toStringList(data[nonMentionedKey])
    }
  }

  return {
    mentionnees: [],
    nonMentionnees: []
  }
}

const hasTranslatedStrings = (source: string[], translated: unknown): boolean => {
  if (source.length === 0) return true
  if (!Array.isArray(translated)) return false

  const translatedItems = translated
    .map(item => (typeof item === 'string' ? item : String(item ?? '')).trim())
    .filter(Boolean)

  return translatedItems.length >= source.length
}

const hasTranslatedLanguages = (source: unknown[], translated: unknown): boolean => {
  const sourceCount = source.filter(item => {
    if (typeof item === 'string') return item.trim().length > 0
    if (item && typeof item === 'object') {
      const language = (item as { language?: string; name?: string }).language
        || (item as { language?: string; name?: string }).name
        || ''
      return language.trim().length > 0
    }
    return false
  }).length

  if (sourceCount === 0) return true
  if (!Array.isArray(translated)) return false

  const translatedCount = translated.filter(item => {
    if (typeof item === 'string') return item.trim().length > 0
    if (item && typeof item === 'object') {
      const language = (item as { language?: string }).language || ''
      return language.trim().length > 0
    }
    return false
  }).length

  return translatedCount >= sourceCount
}

export function isOfferTranslationComplete(
  translationEn: OfferTranslationEn | null | undefined,
  offer: OfferTranslationSource
): boolean {
  if (!translationEn || typeof translationEn !== 'object' || Object.keys(translationEn).length === 0) {
    return false
  }

  const sourceJobTitle = (offer.job_title || '').trim()
  const translatedJobTitle = (translationEn.job_title || '').trim()
  if (sourceJobTitle && !translatedJobTitle) {
    return false
  }
  const snapshotJobTitle = (translationEn.source_job_title || '').trim()
  if (sourceJobTitle && snapshotJobTitle !== sourceJobTitle) {
    return false
  }

  const sourceDescription = (offer.description_text || offer.description || '').trim()
  const translatedDescription = (translationEn.description || '').trim()
  if (sourceDescription && !translatedDescription) {
    return false
  }
  const snapshotDescription = (translationEn.source_description || '').trim()
  if (sourceDescription && snapshotDescription !== sourceDescription) {
    return false
  }

  const requirementsSplit = extractMentionSplit(
    offer.requirements,
    'hard_skills_mentionnées',
    'hard_skills_non_mentionnées'
  )
  const softSkillsSplit = extractMentionSplit(
    offer.soft_skills,
    'soft_skills_mentionnées',
    'soft_skills_non_mentionnées'
  )
  const benefitsSplit = extractMentionSplit(
    offer.benefits,
    'nice_to_have_mentionnées',
    'nice_to_have_non_mentionnées'
  )

  if (!hasTranslatedStrings(requirementsSplit.mentionnees, translationEn.hard_skills)) {
    return false
  }
  if (!hasTranslatedStrings(requirementsSplit.nonMentionnees, translationEn.hard_skills_non_mentionnées)) {
    return false
  }
  if (!hasTranslatedStrings(softSkillsSplit.mentionnees, translationEn.soft_skills)) {
    return false
  }
  if (!hasTranslatedStrings(softSkillsSplit.nonMentionnees, translationEn.soft_skills_non_mentionnées)) {
    return false
  }
  if (!hasTranslatedStrings(benefitsSplit.mentionnees, translationEn.benefits)) {
    return false
  }
  if (!hasTranslatedStrings(benefitsSplit.nonMentionnees, translationEn.benefits_non_mentionnées)) {
    return false
  }
  if (!hasTranslatedStrings(toStringList(offer.certifications), translationEn.certifications)) {
    return false
  }
  if (!hasTranslatedStrings(toStringList(offer.main_missions), translationEn.main_missions)) {
    return false
  }
  if (!hasTranslatedLanguages(Array.isArray(offer.languages) ? offer.languages : [], translationEn.languages)) {
    return false
  }

  return true
}
